import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
    const router = inject(Router);
    const authService = inject(AuthService);

    // Get token from localStorage directly to avoid circular dependency
    const accessToken = localStorage.getItem('access_token');

    // Clone the request to add the authentication header
    let authReq = req;
    if (accessToken && !req.url.includes('/auth/login') && !req.url.includes('/auth/register')) {
        authReq = req.clone({
            setHeaders: {
                Authorization: `Bearer ${accessToken}`
            }
        });
    }

    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 403 && error.error?.details?.code === 'MODULE_DISABLED') {
                // The organization's module set changed since this token/session was last
                // refreshed (e.g. a SUPER_ADMIN just disabled it). Re-sync currentUser$ so
                // the sidebar and route guards reflect it immediately, then bounce away from
                // the now-inaccessible page.
                authService.fetchCurrentUser().subscribe();
                router.navigate(['/dashboard']);
                return throwError(() => error);
            }

            if (error.status === 401 && !authReq.url.includes('/auth/login') && !authReq.url.includes('/auth/refresh')) {
                // Get refresh token from localStorage
                const refreshToken = localStorage.getItem('refresh_token');

                if (!refreshToken) {
                    // No refresh token, redirect to login
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('refresh_token');
                    router.navigate(['/login']);
                    return throwError(() => error);
                }

                // Try to refresh the token
                const http = inject(HttpClient);
                return http.post<{ accessToken: string, refreshToken: string }>(`${environment.apiUrl}/auth/refresh`, { refreshToken }).pipe(
                    switchMap((tokens) => {
                        // Save new tokens
                        localStorage.setItem('access_token', tokens.accessToken);
                        localStorage.setItem('refresh_token', tokens.refreshToken);

                        // Retry the original request with the new token
                        const newAuthReq = req.clone({
                            setHeaders: {
                                Authorization: `Bearer ${tokens.accessToken}`
                            }
                        });
                        return next(newAuthReq);
                    }),
                    catchError((refreshError) => {
                        // Refresh failed, logout
                        localStorage.removeItem('access_token');
                        localStorage.removeItem('refresh_token');
                        router.navigate(['/login']);
                        return throwError(() => refreshError);
                    })
                );
            }
            return throwError(() => error);
        })
    );
};
